import React, { FC } from 'react'
import { Paper } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { Sensor } from 'src/api'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { toInfoColumn } from 'src/components/columns/infoColumn'
import { usePatchSensorRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1,
    fontSize: '13px',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
    overflow: 'visible'
  }
}

const verizonSensorColumns: GridColDef<Sensor>[] = [
  toInfoColumn(SensorColumnsDataGrid.TypeName),
  toInfoColumn(SensorColumnsDataGrid.IMEI),
  toInfoColumn(SensorColumnsDataGrid.Label),
  toInfoColumn(SensorColumnsDataGrid.FirmwareVersion, { headerName: 'Firmware Version' }),
  toInfoColumn(SensorColumnsDataGrid.EnabledBluetooth),
  toInfoColumn(SensorColumnsDataGrid.LastPing),
  toInfoColumn(SensorColumnsDataGrid.LastActiveContractor),
  SensorColumnsDataGrid.Comment,
  toInfoColumn(SensorColumnsDataGrid.StateUpdatedAt),
  toInfoColumn(SensorColumnsDataGrid.StateAuthor),
  toInfoColumn(SensorColumnsDataGrid.ActivationStatus, { headerName: 'ThingSpace Status' })
]

const lorawanSensorColumns: GridColDef<Sensor>[] = [
  toInfoColumn(SensorColumnsDataGrid.TypeName),
  toInfoColumn(SensorColumnsDataGrid.DevEUI),
  toInfoColumn(SensorColumnsDataGrid.ApplicationEUI),
  toInfoColumn(SensorColumnsDataGrid.ApplicationKey),
  toInfoColumn(SensorColumnsDataGrid.BatteryLevel),
  toInfoColumn(SensorColumnsDataGrid.LastPing),
  toInfoColumn(SensorColumnsDataGrid.StateUpdatedAt),
  toInfoColumn(SensorColumnsDataGrid.StateAuthor),
  SensorColumnsDataGrid.Comment
]

interface SensorInfoProps {
  sensor: Sensor
  refetchSensor?: () => void
}

export const SensorInfo: FC<SensorInfoProps> = (props) => {
  const { sensor, refetchSensor } = props
  const addMessage = useAddPopupMessage()
  const updateSensorRequest = usePatchSensorRequest()
  const columns = sensor.imei ? verizonSensorColumns : lorawanSensorColumns
  const gridMinWidth = columns.reduce((sum, column) => sum + (column.minWidth ?? 100), 0)
  const processRowUpdate = async (newRow: Sensor, oldRow: Sensor) => {
    const { comment: newComment } = newRow
    const { comment: oldComment } = oldRow
    const updatedRow: Sensor = await new Promise((resolve) => {
      if (newComment !== oldComment) {
        updateSensorRequest.mutate(
          {
            uniqueId: newRow.uniqueId,
            payload: { comment: newComment }
          },
          {
            onSuccess: () => {
              addMessage({ text: 'The comment updated', type: 'success' })
              if (typeof refetchSensor === 'function') refetchSensor()
              resolve(newRow)
            },
            onError: (error) => {
              addMessage({ text: error?.message, type: 'error' })
              resolve(oldRow)
            }
          }
        )
      }
      return newRow
    })
    return updatedRow
  }

  return (
    <Paper sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGridPremium<Sensor>
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        rows={[sensor]}
        columns={columns}
        getRowId={(row) => row.uniqueId}
        rowThreshold={2}
        columnThreshold={2}
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
        sx={{ ...gridSx, minWidth: gridMinWidth }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          if (error) addMessage({ text: error, type: 'error' })
        }}
      />
    </Paper>
  )
}
